import React, { lazy, Suspense, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { BarChart3, Check, Clock, Flag, LayoutDashboard, Package, Send } from 'lucide-react';
import {
  PROJECT_NAME, PHASES, DELIVERABLES, LAUNCH_CHECKLIST, VELOCITY_STATS,
  OVERALL_PROGRESS, PHASE_STATUS, DELIVERABLE_STATUS,
} from '../../data/dashboardFixture';

const VelocityChart = lazy(() => import('../../components/VelocityChart'));

const TABS = [
  ['overview', 'Overview', LayoutDashboard],
  ['deliverables', 'Deliverables', Package],
  ['launch', 'Launch', Flag],
  ['velocity', 'Velocity', BarChart3],
];

function Bar({ value, tone = 'bg-sky' }) {
  return (
    <div className="h-1.5 w-full overflow-hidden rounded-full bg-white/10">
      <div className={'h-full rounded-full transition-[width] duration-700 ease-expo ' + tone} style={{ width: value + '%' }} />
    </div>
  );
}

function Chip({ meta }) {
  return <span className={'shrink-0 rounded-full px-2 py-0.5 text-[10px] ' + meta.cls}>{meta.label}</span>;
}

function Overview() {
  const [open, setOpen] = useState('build');
  return (
    <div className="space-y-2">
      <div className="rounded-xl border border-line bg-ink/60 p-3">
        <div className="mb-2 flex items-center justify-between text-xs"><span className="text-mist">Overall progress</span><span className="tabular-nums">{OVERALL_PROGRESS}%</span></div>
        <Bar value={OVERALL_PROGRESS} />
      </div>
      {PHASES.map((p) => {
        const meta = PHASE_STATUS[p.status];
        const isOpen = open === p.id;
        return (
          <button key={p.id} type="button" onClick={() => setOpen(isOpen ? null : p.id)} aria-expanded={isOpen} className={'w-full rounded-xl border p-3 text-left transition-colors ' + (isOpen ? 'border-sky/40 bg-cobalt/10' : 'border-line bg-ink/60 hover:border-cream/20')}>
            <div className="flex items-center gap-2">
              <span className={'flex h-5 w-5 shrink-0 items-center justify-center rounded-full ' + (p.status === 'done' ? 'bg-cobalt text-cream' : 'bg-white/10 text-dim')}>
                {p.status === 'done' ? <Check className="h-3 w-3" /> : <span className="h-1.5 w-1.5 rounded-full bg-current" />}
              </span>
              <span className="flex-1 truncate text-xs">{p.label}</span>
              <Chip meta={meta} />
            </div>
            {p.status !== 'next' ? <div className="mt-2 pl-7"><Bar value={p.progress} tone={p.status === 'done' ? 'bg-sky' : 'bg-amber'} /></div> : null}
            {isOpen ? (
              <div className="mt-2 border-t border-line pl-7 pt-2 text-[11px] text-mist">
                <p>{p.details}</p>
                <div className="mt-1.5 flex flex-wrap items-center gap-1">
                  {p.tech.map((t) => <span key={t} className="rounded bg-white/5 px-1.5 py-0.5 text-[10px] text-cream/80">{t}</span>)}
                  <span className="ml-auto inline-flex items-center gap-1 text-dim"><Clock className="h-3 w-3" />{p.eta}</span>
                </div>
              </div>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}

function Deliverables() {
  return (
    <ul className="space-y-1.5">
      {DELIVERABLES.map((d) => (
        <li key={d.label} className="flex items-center justify-between gap-2 rounded-xl border border-line bg-ink/60 px-3 py-2 text-xs">
          <span className="flex min-w-0 items-center gap-2">
            {d.status === 'delivered' ? <Check className="h-3.5 w-3.5 shrink-0 text-sky" /> : <Package className="h-3.5 w-3.5 shrink-0 text-dim" />}
            <span className="truncate">{d.label}</span>
          </span>
          <span className="flex shrink-0 items-center gap-2"><span className="text-[10px] text-dim">{d.date}</span><Chip meta={DELIVERABLE_STATUS[d.status]} /></span>
        </li>
      ))}
    </ul>
  );
}

function Launch() {
  const [requested, setRequested] = useState(false);
  const done = LAUNCH_CHECKLIST.filter((x) => x.done).length;
  return (
    <div className="space-y-2">
      <div className="rounded-xl border border-line bg-ink/60 p-3">
        <div className="mb-2 flex items-center justify-between text-xs"><span className="text-mist">Launch readiness</span><span className="tabular-nums">{done}/{LAUNCH_CHECKLIST.length} ready</span></div>
        <Bar value={(done / LAUNCH_CHECKLIST.length) * 100} tone="bg-amber" />
      </div>
      {LAUNCH_CHECKLIST.map((item) => (
        <div key={item.label} className={'flex items-center gap-2.5 rounded-xl border px-3 py-2 text-xs ' + (item.done ? 'border-sky/30 bg-cobalt/10' : 'border-line bg-ink/60 text-mist')}>
          <span className={'flex h-4 w-4 items-center justify-center rounded border ' + (item.done ? 'border-sky bg-cobalt text-cream' : 'border-line')}>{item.done ? <Check className="h-3 w-3" /> : null}</span>
          {item.label}
        </div>
      ))}
      <div className="flex items-center justify-between gap-3 rounded-xl border border-sky/30 bg-cobalt/10 px-3 py-2.5">
        <div><p className="text-xs font-medium">Target launch</p><p className="text-[11px] text-sky">2 weeks from today</p></div>
        <button type="button" onClick={() => setRequested(true)} className="inline-flex h-8 items-center gap-1.5 rounded-full bg-cream px-3 text-xs font-medium text-ink hover:bg-white">
          <Send className="h-3 w-3" />{requested ? 'Requested' : 'Request launch'}
        </button>
      </div>
    </div>
  );
}

function Velocity() {
  return (
    <div className="space-y-2">
      <div className="grid grid-cols-3 gap-2">
        {VELOCITY_STATS.map((s) => (
          <div key={s.label} className="rounded-xl border border-line bg-ink/60 p-2 text-center">
            <p className="font-display text-lg font-medium tabular-nums">{s.value}</p>
            <p className="text-[10px] text-dim">{s.label}</p>
          </div>
        ))}
      </div>
      <Suspense fallback={<div className="h-40 animate-pulse rounded-xl border border-line bg-ink/60" />}>
        <VelocityChart />
      </Suspense>
    </div>
  );
}

// The client hub in miniature: the same tabs a client gets after signup,
// running on fixture data so visitors can click through it in the hero.
export default function HeroDashboard({ className = '' }) {
  const [tab, setTab] = useState('overview');
  return (
    <div className={'rounded-2xl border border-line bg-deep p-3 shadow-[0_40px_100px_-40px_rgba(29,78,216,0.5)] md:p-4 ' + className}>
      <div className="mb-3 flex items-center justify-between rounded-xl border border-line bg-ink/60 px-3 py-2">
        <div className="flex min-w-0 items-center gap-2">
          <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-cobalt/30 text-sky"><LayoutDashboard className="h-3.5 w-3.5" /></span>
          <span className="truncate text-xs font-medium">Client hub · {PROJECT_NAME}</span>
        </div>
        <span className="flex shrink-0 items-center gap-2 text-[10px] text-dim">
          <span className="inline-flex items-center gap-1 rounded-full bg-cobalt/20 px-2 py-0.5 text-sky"><span className="h-1.5 w-1.5 animate-pulse rounded-full bg-sky" />Live</span>
          {OVERALL_PROGRESS}% done
        </span>
      </div>
      <div className="mb-3 flex gap-1 rounded-xl border border-line bg-ink/60 p-1" role="tablist" aria-label="Client hub sections">
        {TABS.map(([id, label, Icon]) => (
          <button key={id} type="button" role="tab" aria-selected={tab === id} onClick={() => setTab(id)} className={'flex flex-1 items-center justify-center gap-1.5 rounded-lg px-2 py-1.5 text-[11px] transition-colors ' + (tab === id ? 'bg-cream font-medium text-ink' : 'text-mist hover:text-cream')}>
            <Icon className="h-3 w-3 shrink-0" /><span className="hidden sm:inline">{label}</span>
          </button>
        ))}
      </div>
      <div className="max-h-[360px] overflow-y-auto pr-0.5">
        <AnimatePresence mode="wait" initial={false}>
          <motion.div key={tab} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }} transition={{ duration: 0.18 }}>
            {tab === 'overview' ? <Overview /> : null}
            {tab === 'deliverables' ? <Deliverables /> : null}
            {tab === 'launch' ? <Launch /> : null}
            {tab === 'velocity' ? <Velocity /> : null}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
}
